import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Legend from './components/Legend';
import WaffleGrid from './components/WaffleGrid';
import StatsPanel from './components/StatsPanel';
import DataSourcePanel from './components/DataSourcePanel';
import ExportPanel from './components/ExportPanel';
import LoadingSkeleton from './components/LoadingSkeleton';
import Icon from '../../components/AppIcon';
import { fetchMetrics } from '../../data';

const MainVisualizationDashboard = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        
        // Fetch data using centralized function
        const metrics = await fetchMetrics();
        
        const ratio = (metrics?.stablecoins_usd / metrics?.m2_usd) * 100;
        
        setDashboardData({
          stablecoinValue: metrics?.stablecoins_usd,
          m2Value: metrics?.m2_usd,
          lastUpdated: metrics?.last_updated,
          ratio,
          stablecoinPercentage: ratio,
          m2Percentage: 100 - ratio,
          dataQuality: 'high'
        });
        
        setError(null);
      } catch (err) {
        setError('Failed to load dashboard data. Please try again later.');
        console.error('Dashboard data fetch error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();

    // Set up auto-refresh every 15 minutes
    const refreshInterval = setInterval(fetchData, 15 * 60 * 1000);

    return () => clearInterval(refreshInterval);
  }, []);

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Helmet>
          <title>Error - StablecoinM2Tracker</title>
        </Helmet>
        <div className="text-center px-4">
          <div className="flex items-center justify-center w-16 h-16 md:w-20 md:h-20 mx-auto mb-4 rounded-full bg-error/10">
            <Icon name="AlertCircle" size={32} color="var(--color-error)" />
          </div>
          <h1 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground mb-2">
            Unable to Load Dashboard
          </h1>
          <p className="text-sm md:text-base text-muted-foreground mb-6 max-w-md mx-auto">
            {error}
          </p>
          <button
            onClick={() => window.location?.reload()}
            className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium transition-colors duration-300 hover:bg-primary/90"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Helmet>
        <title>Main Dashboard - StablecoinM2Tracker</title>
        <meta 
          name="description" 
          content="Real-time visualization of stablecoin market capitalization relative to US M2 money supply. Track monetary relationships through elegant data visualization." 
        />
      </Helmet>
      <Header />
      <main className="flex-1 pt-16">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
          <div className="mb-6 md:mb-8 lg:mb-12">
            <div className="flex items-center gap-3 mb-3">
              <div className="flex items-center justify-center w-12 h-12 md:w-14 md:h-14 rounded-lg bg-primary/10">
                <Icon name="PieChart" size={28} color="var(--color-primary)" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground">
                  Stablecoin to M2 Visualization
                </h1>
                <p className="text-sm md:text-base text-muted-foreground mt-1">
                  Real-time monetary relationship tracking through elegant data visualization
                </p>
              </div>
            </div>
          </div>

          <StatsPanel 
            stablecoinValue={dashboardData?.stablecoinValue}
            m2Value={dashboardData?.m2Value}
            ratio={dashboardData?.ratio}
          />

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 md:gap-8 mt-6 md:mt-8">
            <div className="lg:col-span-1 space-y-6">
              <Legend 
                stablecoinPercentage={dashboardData?.stablecoinPercentage}
                m2Percentage={dashboardData?.m2Percentage}
              />
              
              <ExportPanel data={dashboardData} />
            </div>

            <div className="lg:col-span-3 space-y-6">
              <WaffleGrid 
                stablecoinPercentage={dashboardData?.stablecoinPercentage}
                totalTiles={100}
              />
              
              <DataSourcePanel />
            </div>
          </div>

          <div className="mt-8 md:mt-12 p-4 md:p-6 bg-primary/5 rounded-lg border border-primary/20">
            <div className="flex items-start gap-3">
              <Icon name="Lightbulb" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-base md:text-lg font-semibold text-foreground mb-2">
                  Understanding the Visualization
                </h3>
                <p className="text-sm md:text-base text-muted-foreground mb-3">
                  Each square in the waffle chart represents 1% the M2 money supply.
                </p>
                <p className="text-sm md:text-base text-muted-foreground">
                  This visualization helps you instantly grasp the relative scale of digital stablecoins compared to traditional monetary supply, and realizing how far this industry has come.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default MainVisualizationDashboard;